import { z } from 'zod';
import { localizedTextSchema, systemRoleKeySchema } from '../access/index';
import { eventSchema } from './envelope';

export const TENANTS_TENANT_CREATED_V1 = 'tenants.tenant.created.v1' as const;
export const TENANTS_TENANT_UPDATED_V1 = 'tenants.tenant.updated.v1' as const;
export const TENANTS_TENANT_SUSPENDED_V1 = 'tenants.tenant.suspended.v1' as const;
export const TENANTS_TENANT_REACTIVATED_V1 = 'tenants.tenant.reactivated.v1' as const;
export const TENANTS_FEATURE_TOGGLED_V1 = 'tenants.feature.toggled.v1' as const;
export const TENANTS_SITE_CREATED_V1 = 'tenants.site.created.v1' as const;
export const TENANTS_THEME_CHANGED_V1 = 'tenants.theme.changed.v1' as const;
export const TENANTS_PLATFORM_BRAND_CHANGED_V1 = 'tenants.platform_brand.changed.v1' as const;
export const TENANTS_ORG_TYPE_CHANGED_V1 = 'tenants.tenant.org_type_changed.v1' as const;
export const TENANTS_TENANT_ARCHIVED_V1 = 'tenants.tenant.archived.v1' as const;
export const TENANTS_TENANT_PURGED_V1 = 'tenants.tenant.purged.v1' as const;
export const TENANTS_PLATFORM_DEFAULT_CHANGED_V1 = 'tenants.platform_default.changed.v1' as const;
export const TENANTS_PLAN_CHANGED_V1 = 'tenants.plan.changed.v1' as const;
export const TENANTS_SUBSCRIPTION_CHANGED_V1 = 'tenants.subscription.changed.v1' as const;
export const TENANTS_ORG_TYPE_PROFILE_CHANGED_V1 = 'tenants.org_type_profile.changed.v1' as const;

/**
 * De namen van de systeemrollen volgens het type organisatie (ORG-3, ADR-0099). `access` maakt de
 * rollen aan en kent de types niet; `tenants` kent de types en maakt geen rollen. Daarom gaan de
 * namen mee in het event. Optioneel: een event zonder namen (van vóór ORG-3, of uit een pad dat het
 * type niet kent) geeft de namen van een fabriek.
 */
const roleNamesField = z.partialRecord(systemRoleKeySchema, localizedTextSchema).optional();

export const tenantCreatedV1Schema = eventSchema({
  tenantId: z.guid(),
  code: z.string(),
  name: z.string(),
  orgType: z.string().optional(),
  roleNames: roleNamesField,
});

export const tenantUpdatedV1Schema = eventSchema({
  tenantId: z.guid(),
  changed: z.array(z.string()),
});

/** ORG-1: het type organisatie is gewijzigd, met de vlaggen die daardoor aan en uit gingen. */
export const tenantOrgTypeChangedV1Schema = eventSchema({
  tenantId: z.guid(),
  from: z.string(),
  to: z.string(),
  turnOn: z.array(z.string()),
  turnOff: z.array(z.string()),
  /**
   * ORG-3: de rolnamen van het nieuwe type. `access` geeft de systeemrollen die naam; een klant kan
   * de naam van een systeemrol niet zelf wijzigen, dus er gaat geen eigen keuze verloren.
   */
  roleNames: roleNamesField,
});

export const tenantSuspendedV1Schema = eventSchema({
  tenantId: z.guid(),
  reason: z.string(),
});

export const tenantReactivatedV1Schema = eventSchema({
  tenantId: z.guid(),
});

export const featureToggledV1Schema = eventSchema({
  tenantId: z.guid(),
  flagKey: z.string(),
  /** `null` betekent: de override is verwijderd, de standaard van de omgeving geldt weer. */
  enabled: z.boolean().nullable(),
});

export type TenantCreatedV1 = z.infer<typeof tenantCreatedV1Schema>;
export type TenantOrgTypeChangedV1 = z.infer<typeof tenantOrgTypeChangedV1Schema>;
export type TenantUpdatedV1 = z.infer<typeof tenantUpdatedV1Schema>;
export type TenantSuspendedV1 = z.infer<typeof tenantSuspendedV1Schema>;
export type TenantReactivatedV1 = z.infer<typeof tenantReactivatedV1Schema>;
export type FeatureToggledV1 = z.infer<typeof featureToggledV1Schema>;

/**
 * Er is een vestiging aangemaakt (Stap 29).
 *
 * Wie hierop hoort te reageren: niemand, nu nog. Het event bestaat omdat het aanmaken van een
 * vestiging een gebeurtenis in de geschiedenis van een bedrijf is - zoals de tenant zelf - en
 * omdat `signage` en `floor-reports` er later standaardinrichting per site aan kunnen hangen.
 */
export const siteCreatedV1Schema = eventSchema({
  siteId: z.guid(),
  code: z.string(),
  name: z.string(),
});

/**
 * De huisstijl van een bedrijf is gewijzigd (MRK-1): kleuren, naam, lettertype, teksten of een
 * afbeelding. Alleen de namen van de velden, niet de waarden: het auditspoor zegt wát er
 * veranderde en wie het deed, en de huidige waarde staat in de huisstijl zelf.
 */
export const themeChangedV1Schema = eventSchema({
  tenantId: z.guid(),
  changed: z.array(z.string()),
});

/**
 * Het platformmerk is gewijzigd (MRK-1). Zonder bedrijf: het geldt voor iedereen. Het auditspoor
 * van een klant slaat het daarom over; het blijft bewaard in de outbox, met de platformbeheerder
 * als actor (ADR-0084).
 */
export const platformBrandChangedV1Schema = eventSchema({
  changed: z.array(z.string()),
});

export type ThemeChangedV1 = z.infer<typeof themeChangedV1Schema>;
export type PlatformBrandChangedV1 = z.infer<typeof platformBrandChangedV1Schema>;

/**
 * Een organisatie is gearchiveerd (MST-2): niemand kan nog aanmelden, en ze staat niet meer in de
 * gewone lijst van Master. Omkeerbaar; terugzetten publiceert `tenants.tenant.reactivated.v1`.
 */
export const tenantArchivedV1Schema = eventSchema({
  tenantId: z.guid(),
  reason: z.string(),
});

/**
 * Een organisatie is definitief gewist (MST-2, ADR-0089). Zonder bedrijf in de envelop: dat bedrijf
 * bestaat niet meer, en alleen het spoor van het platform houdt bij dat het er was.
 */
export const tenantPurgedV1Schema = eventSchema({
  /** Niet `tenantId`: dat veld is van de envelop, en die blijft hier bewust leeg. */
  purgedTenantId: z.guid(),
  code: z.string(),
  name: z.string(),
  /** Hoeveel rijen er in totaal gewist zijn, over alle modules. */
  rows: z.number().int(),
});

/**
 * Master zette de platformstandaard van een functie (MST-4). Zonder bedrijf: het geldt voor elke
 * organisatie zonder eigen instelling. `null` = weer de standaard uit de code.
 */
export const platformDefaultChangedV1Schema = eventSchema({
  flagKey: z.string(),
  enabled: z.boolean().nullable(),
});

/** Master maakte een pakket aan of wijzigde het (MST-6). Zonder bedrijf. */
export const planChangedV1Schema = eventSchema({
  planId: z.guid(),
  key: z.string(),
  action: z.enum(['created', 'updated']),
});

/** Het abonnement van een organisatie is gezet of gewijzigd (MST-6). */
export const subscriptionChangedV1Schema = eventSchema({
  tenantId: z.guid(),
  planKey: z.string(),
  status: z.string(),
});

/**
 * Master wijzigde het startprofiel van een type organisatie (ORG-3, ADR-0099). Zonder bedrijf: het
 * geldt voor elke organisatie die dit type daarna krijgt. `changed` noemt de delen (`flags`,
 * `loginDefault`, `roleNames`); `reset` = alles terug naar de code.
 */
export const orgTypeProfileChangedV1Schema = eventSchema({
  orgType: z.string(),
  changed: z.array(z.string()),
  reset: z.boolean(),
});
export type OrgTypeProfileChangedV1 = z.infer<typeof orgTypeProfileChangedV1Schema>;

export type TenantArchivedV1 = z.infer<typeof tenantArchivedV1Schema>;
export type PlatformDefaultChangedV1 = z.infer<typeof platformDefaultChangedV1Schema>;
export type TenantPurgedV1 = z.infer<typeof tenantPurgedV1Schema>;

export const tenantsEventCatalog = {
  [TENANTS_TENANT_ARCHIVED_V1]: tenantArchivedV1Schema,
  [TENANTS_TENANT_PURGED_V1]: tenantPurgedV1Schema,
  [TENANTS_PLATFORM_DEFAULT_CHANGED_V1]: platformDefaultChangedV1Schema,
  [TENANTS_PLAN_CHANGED_V1]: planChangedV1Schema,
  [TENANTS_SUBSCRIPTION_CHANGED_V1]: subscriptionChangedV1Schema,
  [TENANTS_ORG_TYPE_PROFILE_CHANGED_V1]: orgTypeProfileChangedV1Schema,
  [TENANTS_TENANT_CREATED_V1]: tenantCreatedV1Schema,
  [TENANTS_TENANT_UPDATED_V1]: tenantUpdatedV1Schema,
  [TENANTS_TENANT_SUSPENDED_V1]: tenantSuspendedV1Schema,
  [TENANTS_TENANT_REACTIVATED_V1]: tenantReactivatedV1Schema,
  [TENANTS_FEATURE_TOGGLED_V1]: featureToggledV1Schema,
  [TENANTS_SITE_CREATED_V1]: siteCreatedV1Schema,
  [TENANTS_THEME_CHANGED_V1]: themeChangedV1Schema,
  [TENANTS_PLATFORM_BRAND_CHANGED_V1]: platformBrandChangedV1Schema,
  [TENANTS_ORG_TYPE_CHANGED_V1]: tenantOrgTypeChangedV1Schema,
} as const;
